import React, { useMemo } from 'react';
import { ReactFlow, Background, MarkerType, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { nodeTypes } from './BusinessModelNodes';

const defaultEdgeOptions = {
  type: 'step',
  style: { stroke: '#16a34a', strokeWidth: 3 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#16a34a' }
};

const commonFlowProps = {
  nodeTypes,
  fitView: true,
  fitViewOptions: { padding: 0.15 },
  minZoom: 0.2,
  maxZoom: 1.5,
  nodesDraggable: false,
  nodesConnectable: false,
  elementsSelectable: false,
};

export const OverviewFlow = ({ t }) => {
  const nodes = useMemo(() => [
    { id: 'ov-logo', type: 'esmLogo', position: { x: 475, y: 0 }, data: {} },
    { 
      id: 'ov-tech', 
      type: 'blueHeader', 
      position: { x: 0, y: 150 }, 
      data: { header: t('business_model_tabs.overview.tech_group'), content: t('business_model_tabs.overview.tech_group_desc') }
    },
    { 
      id: 'ov-hsv', 
      type: 'hsvCenter', 
      position: { x: 400, y: 150 }, 
      data: { content: t('business_model_tabs.overview.hsv_desc') }
    },
    { 
      id: 'ov-others', 
      type: 'blueHeader', 
      position: { x: 800, y: 150 }, 
      data: { 
        header: t('business_model_tabs.overview.others'), 
        content: (
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '18px', listStyleType: 'none', padding: '0 20px' }}>
            <li style={{ marginBottom: '8px', fontSize: '18px' }}>- {t('business_model_tabs.overview.others_1')}</li>
            <li style={{ marginBottom: '8px', fontSize: '18px' }}>- {t('business_model_tabs.overview.others_2')}</li>
            <li style={{ marginBottom: '8px', fontSize: '18px' }}>- {t('business_model_tabs.overview.others_3')}</li>
            <li style={{ fontSize: '18px' }}>- {t('business_model_tabs.overview.others_4')}</li>
          </ul>
        )
      }
    }
  ], [t]);

  const edges = useMemo(() => [
    { id: 'e-ov-logo-tech', source: 'ov-logo', target: 'ov-tech', ...defaultEdgeOptions },
    { id: 'e-ov-logo-hsv', source: 'ov-logo', target: 'ov-hsv', ...defaultEdgeOptions },
    { id: 'e-ov-logo-others', source: 'ov-logo', target: 'ov-others', ...defaultEdgeOptions }
  ], []);

  return (
    <div style={{ width: '100%', height: '600px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const ActivitiesFlow = ({ t }) => {
  const nodes = useMemo(() => [
    { id: 'act-root', type: 'greenSolid', position: { x: 0, y: 350 }, data: { label: t('business_model_tabs.activities.title') } },
    { id: 'act-dev', type: 'blueBorder', position: { x: 350, y: 50 }, data: { label: t('business_model_tabs.activities.dev_platform') } },
    { id: 'act-build', type: 'blueBorder', position: { x: 350, y: 350 }, data: { label: t('business_model_tabs.activities.build_esm') } },
    { id: 'act-operate', type: 'blueBorder', position: { x: 350, y: 650 }, data: { label: t('business_model_tabs.activities.operate') } },
    
    // Sub nodes for build
    { id: 'act-b1', type: 'greenBorder', position: { x: 750, y: 50 }, data: { label: t('business_model_tabs.activities.build_1') } },
    { id: 'act-b2', type: 'greenBorder', position: { x: 750, y: 170 }, data: { label: t('business_model_tabs.activities.build_2') } },
    { id: 'act-b3', type: 'greenBorder', position: { x: 750, y: 290 }, data: { label: t('business_model_tabs.activities.build_3') } },
    { id: 'act-b4', type: 'greenBorder', position: { x: 750, y: 410 }, data: { label: t('business_model_tabs.activities.build_4'), width: '380px' } },
    { id: 'act-b5', type: 'greenBorder', position: { x: 750, y: 530 }, data: { label: t('business_model_tabs.activities.build_5'), width: '380px' } },
    { id: 'act-b6', type: 'greenBorder', position: { x: 750, y: 650 }, data: { label: t('business_model_tabs.activities.build_6'), width: '380px' } },
  ], [t]);

  const edges = useMemo(() => [
    { id: 'e-act-root-dev', source: 'act-root', target: 'act-dev', ...defaultEdgeOptions },
    { id: 'e-act-root-build', source: 'act-root', target: 'act-build', ...defaultEdgeOptions },
    { id: 'e-act-root-operate', source: 'act-root', target: 'act-operate', ...defaultEdgeOptions },
    { id: 'e-act-build-b1', source: 'act-build', target: 'act-b1', ...defaultEdgeOptions },
    { id: 'e-act-build-b2', source: 'act-build', target: 'act-b2', ...defaultEdgeOptions },
    { id: 'e-act-build-b3', source: 'act-build', target: 'act-b3', ...defaultEdgeOptions },
    { id: 'e-act-build-b4', source: 'act-build', target: 'act-b4', ...defaultEdgeOptions },
    { id: 'e-act-build-b5', source: 'act-build', target: 'act-b5', ...defaultEdgeOptions },
    { id: 'e-act-build-b6', source: 'act-build', target: 'act-b6', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '850px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const ResourcesFlow = ({ t }) => {
  const nodes = useMemo(() => [
    { id: 'res-root', type: 'greenSolid', position: { x: 0, y: 230 }, data: { label: t('business_model_tabs.resources.title') } },
    { id: 'res-finance', type: 'blueBorder', position: { x: 350, y: 50 }, data: { label: t('business_model_tabs.resources.finance'), width: '320px' } },
    { id: 'res-tech', type: 'blueBorder', position: { x: 350, y: 410 }, data: { label: t('business_model_tabs.resources.tech'), width: '320px' } },
    
    // Sub nodes
    { id: 'res-f1', type: 'greenBorder', position: { x: 800, y: -70 }, data: { label: 'HSV', width: '250px' } },
    { id: 'res-f2', type: 'greenBorder', position: { x: 800, y: 50 }, data: { label: t('business_model_tabs.resources.finance_2'), width: '250px' } },
    { id: 'res-f3', type: 'greenBorder', position: { x: 800, y: 170 }, data: { label: t('business_model_tabs.resources.finance_3'), width: '250px' } },
    
    { id: 'res-t1', type: 'greenBorder', position: { x: 800, y: 290 }, data: { label: t('business_model_tabs.resources.tech_1'), width: '250px' } },
    { id: 'res-t2', type: 'greenBorder', position: { x: 800, y: 410 }, data: { label: 'HSV', width: '250px' } },
    { id: 'res-t3', type: 'greenBorder', position: { x: 800, y: 530 }, data: { label: t('business_model_tabs.resources.tech_3'), width: '250px' } },
  ], [t]);

  const edges = useMemo(() => [
    { id: 'e-res-root-finance', source: 'res-root', target: 'res-finance', ...defaultEdgeOptions },
    { id: 'e-res-root-tech', source: 'res-root', target: 'res-tech', ...defaultEdgeOptions },
    { id: 'e-res-finance-f1', source: 'res-finance', target: 'res-f1', ...defaultEdgeOptions },
    { id: 'e-res-finance-f2', source: 'res-finance', target: 'res-f2', ...defaultEdgeOptions },
    { id: 'e-res-finance-f3', source: 'res-finance', target: 'res-f3', ...defaultEdgeOptions },
    { id: 'e-res-tech-t1', source: 'res-tech', target: 'res-t1', ...defaultEdgeOptions },
    { id: 'e-res-tech-t2', source: 'res-tech', target: 'res-t2', ...defaultEdgeOptions },
    { id: 'e-res-tech-t3', source: 'res-tech', target: 'res-t3', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '750px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const ValueFlow = ({ t }) => {
  const nodes = useMemo(() => [
    { id: 'val-root', type: 'blueSolid', position: { x: 0, y: 410 }, data: { label: t('business_model_tabs.value.title') } },
    
    { id: 'val-v1', type: 'greenBorder', position: { x: 400, y: 50 }, data: { label: t('business_model_tabs.value.v1'), width: '800px' } },
    { id: 'val-v2', type: 'greenBorder', position: { x: 400, y: 170 }, data: { label: t('business_model_tabs.value.v2'), width: '800px' } },
    { id: 'val-v3', type: 'greenBorder', position: { x: 400, y: 290 }, data: { label: t('business_model_tabs.value.v3'), width: '800px' } },
    { id: 'val-v4', type: 'greenBorder', position: { x: 400, y: 410 }, data: { label: t('business_model_tabs.value.v4'), width: '800px' } },
    { id: 'val-v5', type: 'greenBorder', position: { x: 400, y: 530 }, data: { label: t('business_model_tabs.value.v5'), width: '800px' } },
    { id: 'val-v6', type: 'greenBorder', position: { x: 400, y: 650 }, data: { label: t('business_model_tabs.value.v6'), width: '800px' } },
    { id: 'val-v7', type: 'greenBorder', position: { x: 400, y: 770 }, data: { label: t('business_model_tabs.value.v7'), width: '800px' } },
  ], [t]);

  const edges = useMemo(() => [
    { id: 'e-val-root-v1', source: 'val-root', target: 'val-v1', ...defaultEdgeOptions },
    { id: 'e-val-root-v2', source: 'val-root', target: 'val-v2', ...defaultEdgeOptions },
    { id: 'e-val-root-v3', source: 'val-root', target: 'val-v3', ...defaultEdgeOptions },
    { id: 'e-val-root-v4', source: 'val-root', target: 'val-v4', ...defaultEdgeOptions },
    { id: 'e-val-root-v5', source: 'val-root', target: 'val-v5', ...defaultEdgeOptions },
    { id: 'e-val-root-v6', source: 'val-root', target: 'val-v6', ...defaultEdgeOptions },
    { id: 'e-val-root-v7', source: 'val-root', target: 'val-v7', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '1000px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const RelationsFlow = ({ t }) => {
  const c3List = t('business_model_tabs.relations.c3_list', { returnObjects: true });
  const nodes = useMemo(() => [
    { id: 'rel-root', type: 'greenSolid', position: { x: 0, y: 150 }, data: { label: t('business_model_tabs.relations.title') } },
    { id: 'rel-c1', type: 'contentBox', position: { x: 350, y: -50 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.relations.c1') }} /> } },
    { id: 'rel-c2', type: 'contentBox', position: { x: 350, y: 150 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.relations.c2') }} /> } },
    { id: 'rel-c3', type: 'contentBox', position: { x: 350, y: 350 }, data: { 
        label: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: t('business_model_tabs.relations.c3_title') }} />
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              {Array.isArray(c3List) && c3List.map((item, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>- {item}</li>
              ))}
            </ul>
          </div>
        )
      } 
    },
  ], [t, c3List]);

  const edges = useMemo(() => [
    { id: 'e-rel-root-c1', source: 'rel-root', target: 'rel-c1', ...defaultEdgeOptions },
    { id: 'e-rel-root-c2', source: 'rel-root', target: 'rel-c2', ...defaultEdgeOptions },
    { id: 'e-rel-root-c3', source: 'rel-root', target: 'rel-c3', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '750px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const ChannelsFlow = ({ t }) => {
  const c1List = t('business_model_tabs.channels.c1_list', { returnObjects: true });
  const c2List = t('business_model_tabs.channels.c2_list', { returnObjects: true });
  const nodes = useMemo(() => [
    { id: 'chan-root', type: 'greenSolid', position: { x: 0, y: 150 }, data: { label: t('business_model_tabs.channels.title') } },
    { id: 'chan-c1', type: 'contentBox', position: { x: 350, y: -50 }, data: { 
        label: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: t('business_model_tabs.channels.c1_title') }} />
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              {Array.isArray(c1List) && c1List.map((item, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>- {item}</li>
              ))}
            </ul>
          </div>
        )
      } 
    },
    { id: 'chan-c2', type: 'contentBox', position: { x: 350, y: 300 }, data: { 
        label: (
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {Array.isArray(c2List) && c2List.map((item, index) => (
              <li key={index} style={{ marginBottom: '5px' }}>- {item}</li>
            ))}
          </ul>
        )
      } 
    },
  ], [t, c1List, c2List]);

  const edges = useMemo(() => [
    { id: 'e-chan-root-c1', source: 'chan-root', target: 'chan-c1', ...defaultEdgeOptions },
    { id: 'e-chan-root-c2', source: 'chan-root', target: 'chan-c2', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '700px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const SegmentsFlow = ({ t }) => {
  const nodes = useMemo(() => [
    { id: 'seg-root', type: 'blueSolid', position: { x: 0, y: 150 }, data: { label: t('business_model_tabs.segments.title') } },
    { id: 'seg-c1', type: 'contentBox', position: { x: 350, y: -50 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.segments.c1') }} /> } },
    { id: 'seg-c2', type: 'contentBox', position: { x: 350, y: 150 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.segments.c2') }} /> } },
    { id: 'seg-c3', type: 'contentBox', position: { x: 350, y: 350 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.segments.c3') }} /> } },
  ], [t]);

  const edges = useMemo(() => [
    { id: 'e-seg-root-c1', source: 'seg-root', target: 'seg-c1', ...defaultEdgeOptions },
    { id: 'e-seg-root-c2', source: 'seg-root', target: 'seg-c2', ...defaultEdgeOptions },
    { id: 'e-seg-root-c3', source: 'seg-root', target: 'seg-c3', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '700px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export const RevenueFlow = ({ t }) => {
  const c3List = t('business_model_tabs.revenue.c3_list', { returnObjects: true });
  const nodes = useMemo(() => [
    { id: 'rev-root', type: 'greenSolid', position: { x: 0, y: 150 }, data: { label: t('business_model_tabs.revenue.title') } },
    { id: 'rev-c1', type: 'contentBox', position: { x: 350, y: -30 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.revenue.c1') }} /> } },
    { id: 'rev-c2', type: 'contentBox', position: { x: 350, y: 150 }, data: { label: <span dangerouslySetInnerHTML={{ __html: t('business_model_tabs.revenue.c2') }} /> } },
    { id: 'rev-c3', type: 'contentBox', position: { x: 350, y: 300 }, data: { 
        label: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: t('business_model_tabs.revenue.c3_title') }} />
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              {Array.isArray(c3List) && c3List.map((item, index) => (
                <li key={index} style={{ marginBottom: '5px' }} dangerouslySetInnerHTML={{ __html: `- ${item}` }} />
              ))}
            </ul>
          </div>
        )
      } 
    },
  ], [t, c3List]);

  const edges = useMemo(() => [
    { id: 'e-rev-root-c1', source: 'rev-root', target: 'rev-c1', ...defaultEdgeOptions },
    { id: 'e-rev-root-c2', source: 'rev-root', target: 'rev-c2', ...defaultEdgeOptions },
    { id: 'e-rev-root-c3', source: 'rev-root', target: 'rev-c3', ...defaultEdgeOptions },
  ], []);

  return (
    <div style={{ width: '100%', height: '750px' }}>
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} {...commonFlowProps}>
          <Background color="#f8fafc" gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};
